import { useEffect, useState } from 'react';
import type { TerminalSource } from '@orgus/ui';
import { ApiError } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';
import { TermConnection } from './connection.ts';
import type { TermConnState } from './connection.ts';
import { MOCK_YOU, mockTerms } from './mock.ts';

const INITIAL: TermConnState = { phase: 'connecting', term: null, you: null, error: null, exitCode: null };

/** A live connection to one terminal while mounted (demo data in mock mode). */
export function useTerminal(termId: string | null): TermConnState & { source: TerminalSource | null; retry: () => void; notice: ApiError | null; clearNotice: () => void } {
  const mode = useOrgus((s) => s.mode);
  const [st, setSt] = useState<TermConnState>(INITIAL);
  const [source, setSource] = useState<TerminalSource | null>(null);
  const [nonce, setNonce] = useState(0);
  // A refusal the hub sent on the live socket (a write without control, a lost lock): shown inline by the pane.
  const [notice, setNotice] = useState<ApiError | null>(null);

  useEffect(() => {
    setSt(INITIAL);
    if (!termId) {
      setSource(null);
      return;
    }
    if (mode === 'mock') {
      const read = () => {
        const term = mockTerms.get(termId) ?? null;
        setSt({
          phase: term ? 'open' : 'error',
          term,
          you: { actor: MOCK_YOU, canWrite: true, canForce: true },
          error: term ? null : { what: 'That terminal is not open.', why: 'It may have ended.', next: 'Open a new terminal.' },
          exitCode: term?.exitCode ?? null,
        });
      };
      read();
      setSource(mockTerms.source(termId));
      return mockTerms.subscribe(read);
    }
    const c = new TermConnection(termId, {
      onState: setSt,
      onNotice: (e) => setNotice(new ApiError(e)),
    });
    setSource(c);
    c.start();
    return () => {
      c.stop();
      setSource(null);
    };
  }, [termId, mode, nonce]);

  return { ...st, source, retry: () => setNonce((n) => n + 1), notice, clearNotice: () => setNotice(null) };
}
